import { ExploreContext, ZoneData } from "@/components/explore/ExploreContext";
import { DisruptionSection } from "@/components/disruption/DisruptionSection";
import { DisruptionScope } from "@/components/disruption/disruptionMetrics";
import {
  metricTileCardBase,
  metricTileDescription,
  metricTileInner,
  metricTileStandardCard,
  metricTileTitle,
} from "@/components/metrics/metricTileStyles";
import { MetricTileValue } from "@/components/metrics/MetricTileValue";
import { TableView } from "@/components/explore/ExploreTable";
import { trimZones } from "@/components/explore/ExploreView";
import { MetricBreakdownModal } from "@/components/retail/MetricBreakdownModal";
import { getRetailReport, RetailReport, RetailTakeoff, TrendInfo } from "@/queries/retail";
import { useReportContext } from "@/stores/ReportContext";
import { useSessionData } from "@/stores/SessionDataContext";
import { compareGateNames } from "@/lib/gateSort";
import { buildMultiAxis } from "@/viz/chart-data";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import React, { useEffect, useRef, useState } from "react";
import { MetricTileWithTrend } from '@/pages/retail/MetricTileWithTrend';
type TakeoffSortKey = keyof Pick<
  RetailTakeoff,
  "gate_name" | "flight_name" | "sch_dep_time" | "dep_time" | "status"
>;
type SortConfig = {
  key: TakeoffSortKey;
  direction: "asc" | "desc";
};

interface CardProps {
  title: React.ReactNode;
  children: React.ReactNode;
  wrapperClassName?: string;
  innerClassName?: string;
}

function Card({
  title,
  children,
  wrapperClassName = "",
  innerClassName = "",
}: CardProps) {
  return (
    <div
      className={`bg-gradient-to-b from-slate-800 to-slate-900 border border-slate-700 rounded-lg drop-shadow flex flex-col mt-4 h-90 md:h-auto hover:border-indigo-500/20 transition-colors ${wrapperClassName}`}
    >
      <div className="px-5 py-4 text-white border-b border-slate-700 font-medium">
        {title}
      </div>
      <div className={`p-5 ${innerClassName}`}>{children}</div>
    </div>
  );
}

function convertTo15MinIntervalsAndSort(
  gates: ZoneData[] | null
): ZoneData[] | null {
  if (!gates) return null;

  const convertedGates = gates.map((gate) => {
    const newValues: number[] = [];
    const newNotes: (string | null)[] = [];

    for (let i = 0; i < gate.values.length; i += 3) {
      if (i + 2 < gate.values.length) {
        // Full 30-minute block
        newValues.push(gate.values[i]);
        newValues.push(
          Math.round((gate.values[i + 1] + gate.values[i + 2]) / 2)
        );

        newNotes.push(gate.notes[i]);
        newNotes.push(gate.notes[i + 1] || gate.notes[i + 2] || null);
      } else if (i + 1 < gate.values.length) {
        // Last incomplete block (20 minutes)
        newValues.push(gate.values[i]);
        newValues.push(Math.round(gate.values[i + 1] / 2));

        newNotes.push(gate.notes[i]);
        newNotes.push(gate.notes[i + 1] || null);
      } else {
        // Last single 10-minute interval
        newValues.push(gate.values[i]);
        newNotes.push(gate.notes[i]);
      }
    }

    return {
      ...gate,
      values: newValues,
      notes: newNotes,
    };
  });

  // Sort the converted gates by name
  return convertedGates;
}

function GateDisplay({ summary }: { summary: RetailReport }) {
  const { airport_config } = useSessionData();

  // Apply interval conversion *without* sorting
  const convertedZones = convertTo15MinIntervalsAndSort(summary.gates);

  const sortedZones = convertedZones
    ? [...convertedZones].sort((a, b) => compareGateNames(a.name, b.name))
    : [];

  // Trim the naturally sorted zones
  const { zones, times } = trimZones(
    sortedZones,
    airport_config.start_hour,
    airport_config.end_hour,
    15
  );

  return (
    <Card title={"Relevant Gates"} innerClassName={""}>
      <ExploreContext.Provider
        value={{
          zones: zones.reverse(), // Reverse for final display (descending natural sort)
          times,
          concerns: [],
        }}
      >
        <TableView />
      </ExploreContext.Provider>
    </Card>
  );
}

const TRAFFIC_OVERVIEW_INFO =
  "Traffic Overview shows new passengers entering your nearby gate area each hour. It does not include passengers already waiting near the gates. Use Relevant Gates to see passenger activity by gate and time.";

function ChartDisplay({ summary }: { summary: RetailReport }) {
  const [infoVisible, setInfoVisible] = useState(false);
  const infoRef = useRef<HTMLDivElement>(null);

  // Close tooltip when clicking outside
  useEffect(() => {
    function handleOutsideClick(e: MouseEvent) {
      if (infoRef.current && !infoRef.current.contains(e.target as Node)) {
        setInfoVisible(false);
      }
    }
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const chartTitle = (
    <div className="flex items-center gap-2">
      <span>Traffic Overview</span>
      <div ref={infoRef} className="relative flex items-center">
        <button
          onClick={() => setInfoVisible((v) => !v)}
          className="w-4 h-4 rounded-full bg-slate-600 hover:bg-indigo-500 text-white text-xs flex items-center justify-center transition-colors focus:outline-none"
          aria-label="Traffic Overview information"
        >
          i
        </button>
        {infoVisible && (
          <div className="absolute left-6 top-0 z-50 w-72 rounded-lg bg-slate-700 border border-slate-500 shadow-xl p-3 text-xs text-slate-200 leading-relaxed">
            {TRAFFIC_OVERVIEW_INFO}
          </div>
        )}
      </div>
    </div>
  );

  // Guard against missing or incomplete chart data
  if (!summary.chart_hourly_breakdown?.multi_series) {
    return (
      <Card title={chartTitle}>
        <div className="text-center text-gray-400 py-8">
          No chart data available
        </div>
      </Card>
    );
  }

  const chartOptions = buildMultiAxis({
    height: 400,
    data: {
      ...summary.chart_hourly_breakdown,
      total_required: false,
    },
    xAxisTitle: "Time",
    secondaryAxisSeriesIndex: 1,
  });

  // Add custom tooltip formatter to clarify what the value means
  (chartOptions as any).tooltip = {
    shared: true,
    formatter(this: any) {
      if (!this.points) return "";
      return this.points
        .map((pt: any) => {
          const isPassengers = pt.series.name === "New Passenger Inflow";
          const label = isPassengers
            ? `<b>${pt.y}</b> new passengers arriving`
            : `<b>${pt.y}</b> flights`;
          return `<span style="color:${pt.color}">●</span> ${pt.series.name}: ${label}`;
        })
        .join("<br/>");
    },
  };

  return (
    <Card title={chartTitle}>
      <div className="retail-chart-container">
        <style>
          {`
          .retail-chart-container .highcharts-background {
            fill: rgba(0, 0, 0, 0.0);
          }
          `}
        </style>
        <HighchartsReact
          highcharts={Highcharts}
          constructorType={"chart"}
          options={chartOptions}
        />
      </div>
    </Card>
  );
}

interface MetricWithTrend {
  name: string;
  value: number;
  metricKey: string;
  description: string;
}

function MetricTile({
  metric,
  onClick,
}: {
  metric: MetricWithTrend;
  onClick?: () => void;
}) {
  const displayValue = metric.value ?? 0;
  
  return (
    <div
      className={`${metricTileCardBase} ${metricTileStandardCard}`}
      onClick={onClick}
    >
      <div className={metricTileInner}>
        <div className={metricTileTitle}>{metric.name}</div>
        <MetricTileValue value={displayValue} />
        <div className={metricTileDescription}>{metric.description}</div>
      </div>
    </div>
  );
}

function MetricsSection({
  title,
  metrics,
  onMetricClick,
}: {
  title: string;
  metrics: MetricWithTrend[];
  onMetricClick?: (metric: MetricWithTrend) => void;
}) {
  return (
    <div className="mb-6">
      <h2 className="text-white text-sm font-semibold tracking-wider uppercase mb-3 px-1">
        {title}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {metrics.map((metric, index) => (
          <MetricTile
            key={index}
            metric={metric}
            onClick={() => onMetricClick?.(metric)}
          />
        ))}
      </div>
    </div>
  );
}

const sortData = (
  data: RetailTakeoff[],
  sortConfig: SortConfig | null
) => {
  if (!sortConfig) return data;

  return [...data].sort((a, b) => {
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];

    if (aValue === null) return 1;
    if (bValue === null) return -1;

    let comparison = 0;

    // Use natural sort ONLY for gate_name column
    if (sortConfig.key === "gate_name") {
      comparison = compareGateNames(String(aValue), String(bValue));
    } else {
      // Standard lexicographical comparison for other columns
      if (aValue < bValue) {
        comparison = -1;
      } else if (aValue > bValue) {
        comparison = 1;
      } else {
        comparison = 0;
      }
    }

    return sortConfig.direction === "asc" ? comparison : -comparison;
  });
};

const FlightTable = ({ summary }: { summary: RetailReport }) => {
  const [sortConfig, setSortConfig] = useState<SortConfig | null>({
    key: "gate_name",
    direction: "asc",
  });

  const handleSort = (key: TakeoffSortKey) => {
    setSortConfig((currentSort) => {
      if (!currentSort || currentSort.key !== key) {
        return { key, direction: "asc" };
      }
      if (currentSort.direction === "asc") {
        return { key, direction: "desc" };
      }
      return null;
    });
  };

  function displayStatus(status: string | null | undefined) {
    if (!status) return null;

    const statusStyles: Record<string, string> = {
      On: "bg-slate-500/40 text-slate-100 ring-slate-400/50",
      Early: "bg-emerald-500/40 text-emerald-100 ring-emerald-400/50",
      Delayed: "bg-red-500/40 text-red-50 ring-red-400/50",
    };

    const firstWord = status.split(" ")[0];
    const style =
      statusStyles[firstWord] ||
      "bg-slate-500/40 text-slate-100 ring-slate-400/50";

    return (
      <span
        className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${style}`}
      >
        {status}
      </span>
    );
  }

  return (
    <Card
      title="Last Departure Flights of the Day"
      wrapperClassName="w-full h-full"
    >
      <div className="max-h-[38rem] overflow-y-auto overflow-x-auto">
        <table className="w-full flight-table !mt-0 !text-white !overflow-visible">
          <thead>
            <tr className="!text-white font-semibold">
              <th
                onClick={() => handleSort("gate_name")}
                className="sticky top-0 z-10 cursor-pointer bg-slate-700 shadow-[0_1px_0_0_rgb(51_65_85)] hover:bg-slate-800"
              >
                Gate{" "}
                {sortConfig?.key === "gate_name" &&
                  (sortConfig.direction === "asc" ? "↑" : "↓")}
              </th>
              <th
                onClick={() => handleSort("flight_name")}
                className="sticky top-0 z-10 cursor-pointer bg-slate-700 shadow-[0_1px_0_0_rgb(51_65_85)] hover:bg-slate-800"
              >
                Flight{" "}
                {sortConfig?.key === "flight_name" &&
                  (sortConfig.direction === "asc" ? "↑" : "↓")}
              </th>
              <th
                onClick={() => handleSort("sch_dep_time")}
                className="sticky top-0 z-10 cursor-pointer bg-slate-700 shadow-[0_1px_0_0_rgb(51_65_85)] hover:bg-slate-800"
              >
                Scheduled{" "}
                {sortConfig?.key === "sch_dep_time" &&
                  (sortConfig.direction === "asc" ? "↑" : "↓")}
              </th>
              <th
                onClick={() => handleSort("dep_time")}
                className="sticky top-0 z-10 cursor-pointer bg-slate-700 shadow-[0_1px_0_0_rgb(51_65_85)] hover:bg-slate-800"
              >
                Estimated{" "}
                {sortConfig?.key === "dep_time" &&
                  (sortConfig.direction === "asc" ? "↑" : "↓")}
              </th>
              <th
                onClick={() => handleSort("status")}
                className="sticky top-0 z-10 cursor-pointer bg-slate-700 shadow-[0_1px_0_0_rgb(51_65_85)] hover:bg-slate-800"
              >
                Status{" "}
                {sortConfig?.key === "status" &&
                  (sortConfig.direction === "asc" ? "↑" : "↓")}
              </th>
            </tr>
          </thead>
          <tbody>
            {summary.takeoffs &&
              sortData(summary.takeoffs, sortConfig).map((flight) => (
                <tr key={flight.gate_name} className="hover:bg-slate-800/50">
                  <td>
                    {flight.gate_guess_flag ? (
                      <span className="opacity-90 !text-gray-100">
                        *{flight.gate_name}
                      </span>
                    ) : (
                      <span className="!text-white font-medium">
                        {flight.gate_name}
                      </span>
                    )}
                  </td>
                  <td className="!text-white font-medium">
                    {flight.flight_name || <span className="text-slate-500">-</span>}
                  </td>
                  <td className="!text-white">
                    {flight.sch_dep_time || <span className="text-slate-500">-</span>}
                  </td>
                  <td className="!text-white">
                    {flight.dep_time || <span className="text-slate-500">-</span>}
                  </td>
                  <td>
                    {flight.departure_status_display ? (
                      displayStatus(flight.departure_status_display)
                    ) : (
                      <span className="text-slate-500">-</span>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {summary.takeoffs &&
        summary.takeoffs.some((flight) => flight.gate_guess_flag) && (
          <div className="opacity-90 font-mono text-xs mt-4 !text-gray-100">
            Gates marked with &quot;*&quot; show temporary assignments based on
            historical data. These will be updated once final gates are
            confirmed.
          </div>
        )}
      {summary.takeoffs &&
        summary.takeoffs.some((flight) => !flight.flight_name) && (
          <div className="opacity-90 font-mono text-xs mt-2 !text-gray-100">
            Gates showing &quot;-&quot; have no scheduled departure flights but are included in your service area.
          </div>
        )}
    </Card>
  );
};

function retailDisruptionScope(summary: RetailReport): DisruptionScope {
  const gateNames =
    summary.gates?.map((gate) => gate.name).filter((name): name is string => Boolean(name)) ??
    summary.service_gate_names?.filter((name): name is string => Boolean(name));
  const terminalNames = summary.primary_terminal
    ? [summary.primary_terminal]
    : undefined;

  return {
    gateNames: gateNames?.length ? gateNames : undefined,
    terminalNames,
  };
}

function retailScopeLabel(stores: number[]): string {
  if (stores.length === 1) {
    return `Service area · Store ${stores[0]}`;
  }
  return `Service area · ${stores.length} stores`;
}

const SingleStoreReport: React.FC<{
  summary: RetailReport;
  stores: number[];
}> = ({ summary, stores }) => {
  const { reportId, report } = useReportContext();
  const [breakdownModalOpen, setBreakdownModalOpen] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState<MetricWithTrend | null>(null);

  const terminalTotals: MetricWithTrend[] = [
    {
      name: "Total Terminal Flights",
      description: "All scheduled flights for today",
      value: summary.total_flights,
      metricKey: "total_flights",
    },
    {
      name: "Total Terminal Passengers",
      description: "All passengers for today",
      value: summary.total_pax,
      metricKey: "total_pax",
    },
    {
      name: "Service Area Departures",
      description: "Departures in your service area",
      value: summary.service_flights,
      metricKey: "service_flights",
    },
  ];

  const serviceAreaTotals: MetricWithTrend[] = [
    {
      name: "Flights Remaining Today",
      description: "Remaining departures today",
      value: summary.remain_dep_flights,
      metricKey: "remain_dep_flights",
    },
    {
      name: "Passengers Still Approaching",
      description: "Not yet in the service area",
      value: summary.remaining_pax_dep,
      metricKey: "remaining_pax_dep",
    },
    {
      name: "Total Service Area Passengers",
      description: "All passengers in your service area",
      value: summary.service_pax,
      metricKey: "service_pax",
    },
  ];

  const liveFlowMetrics: Array<{
    name: string;
    value: number;
    metricKey: string;
    description: string;
    trend?: TrendInfo | null;
  }> = [
    {
      name: "Flights Next 60 Min",
      description: "Departures in the next 60 minutes",
      value: summary.hour_flights ?? 0,
      metricKey: "hour_flights",
      trend: summary.hour_flights_trend
    },
    {
      name: "Passengers Next 60 Min",
      description: "Expected to enter service area",
      value: summary.hour_pax ?? 0,
      metricKey: "hour_pax",
      trend: summary.hour_pax_trend
    },
    {
      name: "Passengers in Service Area Now",
      description: "Currently in the service area",
      value: summary.current_pax ?? 0,
      metricKey: "current_pax",
      trend: summary.current_pax_trend
    },
  ];

  const handleMetricClick = (metric: MetricWithTrend) => {
    setSelectedMetric(metric);
    setBreakdownModalOpen(true);
  };

  return (
    <div className="w-full">
      <div className="flex flex-col xl:flex-row gap-4">
        {summary.takeoffs && summary.takeoffs.length > 0 && (
          <FlightTable summary={summary} />
        )}
        
        <div className="w-full">
          <MetricsSection
            title="TERMINAL TOTALS"
            metrics={terminalTotals}
            onMetricClick={handleMetricClick}
          />
          
          <MetricsSection
            title="SERVICE AREA TOTALS"
            metrics={serviceAreaTotals}
            onMetricClick={handleMetricClick}
          />
          
          <div className="mb-6">
            <h2 className="text-white text-sm font-semibold tracking-wider uppercase mb-3 px-1">
              LIVE FLOW & NEAR-TERM ACTIVITY
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {liveFlowMetrics.map((metric, index) => 
              metric.trend ? (
                <MetricTileWithTrend
                  key={index}
                  metric={{
                    name: metric.name,
                    value: metric.value,
                    description: metric.description,
                    metricKey: metric.metricKey  // Add this
                  }}
                  trend={metric.trend}
                  onClick={() => handleMetricClick(metric)}
                />
              ) : (
                <MetricTile
                  key={index}
                  metric={metric}
                  onClick={() => handleMetricClick(metric)}
                />
              )
            )}
            </div>
          </div>
        </div>
      </div>

      <div className="w-full highcharts-dark mt-8">
        <ChartDisplay summary={summary} />
      </div>
      

      {summary.gates && <GateDisplay summary={summary} />}
      <DisruptionSection
        summary={summary.summary_canceled ?? null}
        reportId={reportId}
        reportType={report?.type}
        scopeLabel={retailScopeLabel(stores)}
        scope={retailDisruptionScope(summary)}
      />
      <MetricBreakdownModal
        open={breakdownModalOpen}
        onOpenChange={setBreakdownModalOpen}
        reportId={Number(reportId)}
        metricName={selectedMetric?.metricKey || null}
        metricTitle={selectedMetric?.name || null}
        metricValue={selectedMetric?.value || null}
        stores={stores}
      />
    </div>
  );
};

const AllStoresReport: React.FC<{
  summary: RetailReport;
  stores: number[];
}> = ({ summary, stores }) => {
  const { code_iata } = useSessionData();
  const { formattedDate, reportId, report } = useReportContext();
  const [breakdownModalOpen, setBreakdownModalOpen] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState<MetricWithTrend | null>(null);

  const metrics: MetricWithTrend[] = [
    {
      name: `Total Flights @ ${code_iata}`,
      description: `All scheduled flights for today`,
      value: summary.total_flights,
      metricKey: "total_flights",
    },
    {
      name: `Total Passengers @ ${code_iata}`,
      description: "All passengers for today",
      value: summary.total_pax,
      metricKey: "total_pax",
    },
    {
      name: "Departures in Service Area",
      description: "Departures in service area",
      value: summary.service_flights,
      metricKey: "service_flights",
    },
    {
      name: "Remaining Departures",
      description: "Remaining departures today",
      value: summary.remain_dep_flights,
      metricKey: "remain_dep_flights",
    },
    {
      name: "Remaining DEP Passengers",
      description: "Not yet in the service area",
      value: summary.remaining_pax_dep,
      metricKey: "remaining_pax_dep",
    },
    {
      name: "Remaining Pax (Airport)",
      description: "Remaining at airport",
      value: summary.remain_pax,
      metricKey: "remain_pax",
    },
  ].filter((metric) => metric.value !== null && metric.value !== undefined);

  const handleMetricClick = (metric: MetricWithTrend) => {
    setSelectedMetric(metric);
    setBreakdownModalOpen(true);
  };

  return (
    <div className="w-full">
      <div className="text-center text-xl sm:text-3xl text-indigo-400 mt-4 mb-6">
        {code_iata} Overview for {formattedDate}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
        {metrics.map((metric, index) => (
          <MetricTile
            key={index}
            metric={metric}
            onClick={() => handleMetricClick(metric)}
          />
        ))}
      </div>

      <div className="w-full highcharts-dark">
        <ChartDisplay summary={summary} />
      </div>
      <DisruptionSection
        summary={summary.summary_canceled ?? null}
        reportId={reportId}
        reportType={report?.type}
        scopeLabel={retailScopeLabel(stores)}
        scope={retailDisruptionScope(summary)}
      />
      <MetricBreakdownModal
        open={breakdownModalOpen}
        onOpenChange={setBreakdownModalOpen}
        reportId={Number(reportId)}
        metricName={selectedMetric?.metricKey || null}
        metricTitle={selectedMetric?.name || null}
        metricValue={selectedMetric?.value || null}
        stores={stores}
      />
    </div>
  );
};

function RetailSummary({
  stores,
  allStores = false,
}: {
  stores: number[];
  allStores?: boolean;
}) {
  const { reportId } = useReportContext();
  const { restrict_store } = useSessionData();

  const singleStore = !!restrict_store;

  const { data: summary } = getRetailReport.useQuery({
    report: Number(reportId),
    stores,
    allStores,
  });

  useEffect(() => {
    if (summary) {
      const storeNames =
        stores.length === 1 ? `Store ${stores[0]}` : "All Stores";
      document.title = `Retail Summary | ${storeNames}`;
    }
  }, [summary, stores]);

  if (!summary) {
    return null;
  }

  if (stores.length === 1 || singleStore) {
    return <SingleStoreReport summary={summary} stores={stores} />;
  } else {
    return <AllStoresReport summary={summary} stores={stores} />;
  }
}

export default RetailSummary;
